import React, { useEffect, useState } from "react";
import OutfitDetailPanel from "container/outfitDetail/OutfitDetailPanel";
import ItemAdderPanel from "container/upload/ItemAdderPanel";
import DateSetUpPanel from "container/upload/DateSetUpPanel";
import OutfitSummaryPanel from "container/upload/OutfitSummaryPanel";
import RootItemUpload from "container/upload/RootItemUpload";
import LoadingModalView from "presentational/common/modals/LoadingModalView";
import TXT from "presentational/common/multinational/TXT";
import { useHistory } from "react-router-dom";
import { withStore } from "store/storeHandler";
import { getImgUrl } from "lib/imageUrlHelper";
import { withToken } from "lib/forceTokenRenewComponent";
import PropTypes from "prop-types";

RootOutfitDetail.propTypes = {
  outfitHash: PropTypes.string.isRequired
};

function RootOutfitDetail(props) {
  const { outfitHash } = props;
  const {
    username,
    hasLoggedIn,
    infoData,
    itemData,
    calendarData,
    MyOutfitActions,
    OutfitDetailActions,
    AppErrorActions,
    NoticeModalActions
  } = props;
  const { forceRenewTokenWT, getIdTokenWT } = props;
  const [mode, setMode] = useState("detailView");
  const [itemList, setItemList] = useState([]);
  const [calendarDate, setCalendarDate] = useState("");
  const [calendarAgreement, setCalendarAgreement] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const history = useHistory();

  useEffect(() => {
    requestOutfitDetailData();

    return () => {
      deleteExportedData();
    };
  }, []);

  const displayNotice = type => {
    let content;
    switch (type) {
      case "unknownOutfitError":
        content = <TXT name="msg_outfitNotExist" />;
        break;
      default:
        throw Error(`typeError => type:"${type}"`);
    }
    NoticeModalActions.openModal({ content });
  };

  const requestOutfitDetailData = async () => {
    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;
    try {
      await OutfitDetailActions.changeOutfitDetail({ outfitHash, idToken });
    } catch (error) {
      AppErrorActions.setFatalAppError(error);
    }
  };

  const getTypeOfUser = () => {
    if (!hasLoggedIn) return "guest";
    else if (!infoData.ownerName) return "pending";
    else if (infoData.ownerName === username) return "owner";
    else return "others";
  };

  const deleteExportedData = () => {
    OutfitDetailActions.clearExportedData();
  };

  const goToDetailMode = () => {
    setMode("detailView");
  };

  const goToEditMode = () => {
    window.scrollTo(0, 0);
    setMode("editView");
  };

  const goToDateSetMode = () => {
    window.scrollTo(0, 0);
    setMode("dateSetView");
  };

  const goToItemUploadMode = () => {
    window.scrollTo(0, 0);
    setMode("itemUploadView");
  };

  const goToSummaryMode = () => {
    window.scrollTo(0, 0);
    setMode("summaryView");
  };

  const handleEditModeSelect = () => {
    const { date, isDisplay } = calendarData;
    setItemList(itemData);
    setCalendarDate(date);
    setCalendarAgreement(isDisplay);
    goToEditMode();
  };

  const handleItemSelect = itemList => {
    setItemList(itemList);
  };

  const handleCalendarDateChange = dateStr => {
    setCalendarDate(dateStr);
  };

  const handleCalendarAgreementChange = inputBool => {
    setCalendarAgreement(inputBool);
  };

  const handleSubmitClick = async () => {
    const itemArray = itemList.map(itemObj => itemObj.itemHash);

    if (isUploading) return null;

    await setIsUploading(true);

    const hasSucceed = await forceRenewTokenWT();
    if (!hasSucceed) {
      setIsUploading(false);
      return null;
    }

    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) {
      setIsUploading(false);
      return null;
    }

    try {
      const code = await OutfitDetailActions.rewriteItemOfOutfit({
        outfitHash,
        itemArray,
        calendarDate,
        calendarAgreement,
        idToken
      });
      if (code === "UnknownOutfit") {
        displayNotice("unknownOutfitError");
      }
    } catch (error) {
      AppErrorActions.setFatalAppError(error);
      setIsUploading(false);
      return null;
    }
    setIsUploading(false);
    goToDetailMode();
  };

  const handleDeleteClick = async () => {
    const hasSucceed = await forceRenewTokenWT();
    if (!hasSucceed) return null;

    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) return null;

    if (isDeleting) return null;

    await setIsDeleting(true);

    try {
      await MyOutfitActions.deleteOutfit({ outfitHash, idToken });
    } catch (error) {
      AppErrorActions.setFatalAppError(error);
      setIsDeleting(false);
      return null;
    }
    setIsDeleting(false);
    OutfitDetailActions.deleteOutfitDetail(outfitHash);
    MyOutfitActions.deleteAllData();
    MyOutfitActions.setRealtimeDataOnNextRequest();
    history.goBack();
  };

  const render = () => {
    const imageSrc = getImgUrl("outfit", infoData.outfitImage);

    return (
      <div>
        {mode === "detailView" && (
          <OutfitDetailPanel
            outfitHash={outfitHash}
            typeOfUser={getTypeOfUser()}
            onEditModeSelect={handleEditModeSelect}
            onDeleteClick={handleDeleteClick}
          />
        )}
        {mode === "editView" && getTypeOfUser() === "owner" && (
          <ItemAdderPanel
            mode="outfitEdit"
            stickeredImageDataURL={imageSrc}
            selectedItemList={itemList}
            onBackClick={goToDetailMode}
            onNextClick={goToDateSetMode}
            onItemUploadBtnClick={goToItemUploadMode}
            onItemSelect={handleItemSelect}
          />
        )}
        {mode === "dateSetView" && getTypeOfUser() === "owner" && (
          <DateSetUpPanel
            stickeredImageDataURL={imageSrc}
            calendarDate={calendarDate}
            calendarAgreement={calendarAgreement}
            onCalendarDateChange={handleCalendarDateChange}
            onCalendarAgreementChange={handleCalendarAgreementChange}
            onBackClick={goToEditMode}
            onNextClick={goToSummaryMode}
          />
        )}
        {mode === "summaryView" && getTypeOfUser() === "owner" && (
          <OutfitSummaryPanel
            stickeredImageDataURL={imageSrc}
            itemList={itemList}
            calendarDate={calendarDate}
            calendarAgreement={calendarAgreement}
            onBackClick={goToDateSetMode}
            onSubmitClick={handleSubmitClick}
          />
        )}
        {mode === "itemUploadView" && getTypeOfUser() === "owner" && (
          <RootItemUpload onExitClick={goToEditMode} />
        )}
        <LoadingModalView open={isUploading} />
      </div>
    );
  };

  return render();
}

export default withStore({
  stateMap: {
    myAccount: ["username", "hasLoggedIn"],
    outfitDetail: ["infoData", "itemData", "calendarData"]
  },
  dispatchMap: ["myOutfit", "outfitDetail", "appError", "noticeModal"]
})(withToken(RootOutfitDetail));
